import { useEffect, useState } from "react"
import { useRouter } from "@tanstack/react-router"

import { getReportById, REPORTS, ReportIds, useUserReports, type ReportId } from "@/features/today"
import { useWebApp } from "@/features/webapp"

import { downloadReportPdf, getReportPdfUrl } from "../config/report-pdf"
import { useReportPdf } from "../hooks/use-report-pdf"
import { ReportViewNavbar } from "./report-view-navbar"
import { ReportPdfPreview } from "./report-pdf-preview"
import { KeepYourReportSection } from "./keep-your-report-section"
import { GuidanceSection } from "./guidance-section"
import { ContinueJourneySection } from "./continue-journey-section"

interface ReportViewPageProps {
  /** Which report to show (by id, from the `?report=` search param). */
  reportId?: ReportId
}

/**
 * Full-screen purchased-report view: PDF first-page preview + download + advisors
 * + cross-sell. Full-bleed, so the shell header is hidden while it is mounted.
 */
export function ReportViewPage({ reportId }: ReportViewPageProps) {
  const router = useRouter()
  const { setHeaderHidden } = useWebApp()
  const [pdfReady, setPdfReady] = useState(false)

  const report = (reportId && getReportById(reportId)) || REPORTS[0]

  // The backend is the source of the report url. Love Match is backend-ONLY:
  // no local fallback — if it isn't ready yet we wait (placeholder). Other
  // reports may fall back to the bundled sample while the API has no entry.
  const { byType, loading: reportsLoading } = useUserReports()
  const apiReport = byType(report.id)
  const apiUrl = apiReport?.status === "ready" ? apiReport.url : null
  const backendOnly = report.id === ReportIds.LoveMatch || report.id === ReportIds.AstroMap
  const pending =
    apiReport?.status === "processing" ||
    (reportsLoading && !apiReport) ||
    (backendOnly && !apiUrl)
  const pdfUrl = apiUrl ?? (pending || backendOnly ? "" : getReportPdfUrl(report.id))

  const { fileSizeLabel } = useReportPdf(pdfUrl)

  useEffect(() => {
    setHeaderHidden(true)
    return () => setHeaderHidden(false)
  }, [setHeaderHidden])

  const handleDownload = () =>
    downloadReportPdf(pdfUrl, `${report.title.replace(/\s+/g, "-").toLowerCase()}.pdf`)

  return (
    <div className="-mx-4 bg-dark-base">
      <ReportViewNavbar
        sizeLabel={fileSizeLabel}
        loading={!pdfReady}
        onBack={() => router.history.back()}
        onDownload={handleDownload}
      />

      <ReportPdfPreview pdfUrl={pdfUrl} onReady={() => setPdfReady(true)} />

      <div className="flex flex-col gap-10 px-4 py-10">
        <KeepYourReportSection
          onDownload={handleDownload}
          sizeLabel={fileSizeLabel}
          loading={!pdfReady}
        />

        <div className="h-px w-full bg-purple-light/10" />

        <GuidanceSection />

        <ContinueJourneySection currentId={report.id} />
      </div>
    </div>
  )
}
